/**
 * HTML для PDF тижневого рапорту водія (друк через Puppeteer, printBackground).
 */

export function escapeHtml(s) {
  if (s == null || s === undefined) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Число з рядка значення рапорту (PLN, коми, «—»). */
function parseCalculationNumeric(valueStr) {
  if (valueStr == null) return NaN;
  let s = String(valueStr).trim().replace(/\u00a0/g, " ");
  if (s === "" || s === "—" || s === "–") return NaN;
  s = s.replace(/PLN/gi, "").replace(/\s/g, "").replace(",", ".");
  const m = s.match(/-?\d+(?:\.\d+)?/);
  if (!m) return NaN;
  return parseFloat(m[0]);
}

function isCalcValueZero(n) {
  if (Number.isNaN(n)) return false;
  return Math.abs(n) < 1e-9;
}

const SERVICE_THEME = {
  uber: { letter: "U", headBg: "#111827", badgeBg: "#1f2937", badgeFg: "#f9fafb" },
  bolt: { letter: "B", headBg: "#059669", badgeBg: "#34d399", badgeFg: "#022c22" },
  freenow: { letter: "F", headBg: "#0f766e", badgeBg: "#2dd4bf", badgeFg: "#042f2e" },
};

function serviceSectionHtml(section) {
  const theme = SERVICE_THEME[section.serviceKey] || SERVICE_THEME.uber;
  const rowsHtml = (section.rows || [])
    .map(
      (r) => `<tr>
        <td>${escapeHtml(r.label)}</td>
        <td>${escapeHtml(r.value)}</td>
      </tr>`
    )
    .join("");

  return `<section class="card service-card">
    <header class="card-head service-card-head" style="background:${theme.headBg};">
      <span class="svc-badge" style="background:${theme.badgeBg};color:${theme.badgeFg};">${theme.letter}</span>
      <span class="svc-title">${escapeHtml(section.title)}</span>
    </header>
    <table class="data">
      <tbody>
        ${rowsHtml}
        <tr class="row-strong">
          <td>${escapeHtml(section.taxLabel)}</td>
          <td>${escapeHtml(section.taxValue)}</td>
        </tr>
        <tr class="row-total">
          <td>${escapeHtml(section.totalLabel)}</td>
          <td>${escapeHtml(section.totalValue)}</td>
        </tr>
      </tbody>
    </table>
  </section>`;
}

/** Uber / Bolt / FreeNow в один ряд — менша висота сторінки. */
function servicesRowHtml(sections) {
  if (!sections || sections.length === 0) return "";
  const n = Math.min(3, sections.length);
  const gridStyle = `grid-template-columns: repeat(${n}, minmax(0, 1fr));`;
  return `<div class="services-row" style="${gridStyle}">${sections.map(serviceSectionHtml).join("")}</div>`;
}

/**
 * @param {{ payoutLabel: string, payoutValue: string, salaryLabel: string, salaryValue: string }} [summary]
 */
function calculationSectionHtml(title, rows, summary) {
  const body = (rows || [])
    .map((r) => {
      const n = parseCalculationNumeric(r.value);
      if (!Number.isNaN(n) && isCalcValueZero(n)) return null;

      const isPos = !Number.isNaN(n) && n > 0;
      const isNeg = !Number.isNaN(n) && n < 0;
      const trClass = isPos ? "calc-row-pos" : isNeg ? "calc-row-neg" : "calc-row-neutral";
      const signHtml = isPos
        ? `<span class="calc-sign calc-sign-plus" aria-hidden="true">+</span>`
        : isNeg
          ? `<span class="calc-sign calc-sign-minus" aria-hidden="true">−</span>`
          : `<span class="calc-sign calc-sign-empty" aria-hidden="true"></span>`;

      const debtWeekLine =
        r.debtCalc && r.debtWeek != null && String(r.debtWeek).trim()
          ? `<div class="debt-calc-week">${escapeHtml(String(r.debtWeek).trim())}</div>`
          : "";

      const labelInner =
        r.debtCalc && r.debtLabelLine
          ? `<div class="debt-calc-block">
              <div class="debt-calc-title">${escapeHtml(r.debtLabelLine)}</div>
              ${debtWeekLine}
              <div class="debt-calc-desc">${escapeHtml(r.debtDesc || "—")}</div>
            </div>`
          : `<span class="calc-label-text">${escapeHtml(r.label)}</span>`;

      return `<tr class="${trClass}">
        <td class="calc-label-cell">${signHtml}${labelInner}</td>
        <td>${escapeHtml(r.value)}</td>
      </tr>`;
    })
    .filter(Boolean)
    .join("");

  const summaryHtml =
    summary &&
    summary.payoutLabel &&
    summary.salaryLabel &&
    summary.payoutValue != null &&
    summary.salaryValue != null
      ? `<div class="calc-summary">
          <div class="calc-summary-row calc-summary-payout">
            <span>${escapeHtml(summary.payoutLabel)}</span>
            <strong>${escapeHtml(summary.payoutValue)} PLN</strong>
          </div>
          <div class="calc-summary-row calc-summary-salary">
            <span>${escapeHtml(summary.salaryLabel)}</span>
            <strong>${escapeHtml(summary.salaryValue)} PLN</strong>
          </div>
        </div>`
      : "";

  return `<section class="card calc-card">
    <header class="card-head calc-head">
      <span class="icon-calc" aria-hidden="true">∑</span>
      <span>${escapeHtml(title)}</span>
    </header>
    <table class="data">
      <tbody>${body}</tbody>
    </table>
    ${summaryHtml}
  </section>`;
}

function debtsSectionHtml(title, rows) {
  if (!rows || rows.length === 0) return "";
  const body = rows
    .map((r) => {
      if (r.openDebt) {
        return `<tr class="debts-open-row">
        <td class="debts-open-left">
          <div class="debts-open-title">${escapeHtml(r.titleLine || "")}</div>
          <div class="debts-open-desc">${escapeHtml(r.descLine != null ? r.descLine : "—")}</div>
        </td>
        <td class="debts-open-value">${escapeHtml(r.value)}</td>
      </tr>`;
      }
      return `<tr>
        <td>${escapeHtml(r.label)}</td>
        <td>${escapeHtml(r.value)}</td>
      </tr>`;
    })
    .join("");

  return `<section class="card debts-card">
    <header class="card-head debts-head">
      <span class="icon-warn" aria-hidden="true">!</span>
      <span>${escapeHtml(title)}</span>
    </header>
    <table class="data debts-table">
      <tbody>${body}</tbody>
    </table>
  </section>`;
}

/**
 * @param {object} p
 * @param {string} p.driverLabel
 * @param {string} p.weekLine — наприклад "Тиждень: 12 · 18.03–24.03"
 * @param {Array<{ serviceKey: 'uber'|'bolt'|'freenow', title: string, rows: {label:string,value:string}[], taxLabel, taxValue, totalLabel, totalValue }>} p.services
 * @param {string} p.calculationTitle
 * @param {Array<{ label: string, value: string } | { debtCalc: true, debtLabelLine: string, debtWeek?: string, debtDesc: string, value: string }>} p.calculationRows
 * @param {string} [p.debtsTitle]
 * @param {Array<{ label: string, value: string } | { openDebt: true, titleLine: string, descLine: string, value: string }>} [p.debtRows]
 * @param {string} [p.footerLine] — дрібний текст унизу (наприклад дата генерації)
 * @param {{ payoutLabel: string, payoutValue: string, salaryLabel: string, salaryValue: string }} [p.calcSummary] — підсумок після таблиці
 */
export function buildDriverWeeklyReportPdfHtml(p) {
  const servicesHtml = servicesRowHtml(p.services || []);
  const calcHtml = calculationSectionHtml(p.calculationTitle, p.calculationRows || [], p.calcSummary);
  const debtsHtml = debtsSectionHtml(p.debtsTitle, p.debtRows);

  return `<!DOCTYPE html>
<html lang="uk">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(p.driverLabel)} — ${escapeHtml(p.weekLine)}</title>
  <style>
    * { box-sizing: border-box; }
    @page { margin: 16mm; size: A4; }
    body {
      margin: 0;
      font-family: "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 10.5pt;
      line-height: 1.35;
      color: #1e293b;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .wrap { max-width: 100%; }
    .hero {
      text-align: center;
      padding: 18px 16px 20px;
      margin-bottom: 18px;
      border-radius: 10px;
      background: linear-gradient(125deg, #1e3a5f 0%, #2563eb 48%, #0d9488 100%);
      color: #f8fafc;
      box-shadow: 0 4px 14px rgba(30, 58, 95, 0.25);
    }
    .hero .driver { font-size: 17pt; font-weight: 700; letter-spacing: 0.02em; }
    .hero .week { margin-top: 8px; font-size: 11pt; opacity: 0.95; }
    .services-row {
      display: grid;
      gap: 8px;
      margin-bottom: 12px;
      align-items: stretch;
    }
    .services-row .service-card {
      margin-bottom: 0;
      min-width: 0;
    }
    .services-row .service-card-head {
      padding: 5px 8px;
      font-size: 9pt;
      gap: 6px;
    }
    .services-row .svc-badge {
      width: 22px;
      height: 22px;
      font-size: 8.5pt;
    }
    .services-row .svc-title { font-size: 9pt; }
    .services-row table.data td {
      padding: 3px 6px;
      font-size: 8.5pt;
      line-height: 1.25;
    }
    .services-row table.data td:first-child {
      word-break: break-word;
      hyphens: auto;
    }
    .card {
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      margin-bottom: 14px;
      overflow: hidden;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
    }
    .card-head {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 14px;
      font-weight: 700;
      font-size: 11pt;
      color: #fff;
    }
    .svc-badge {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      font-size: 11pt;
      flex-shrink: 0;
    }
    .svc-title { letter-spacing: 0.02em; }
    table.data { width: 100%; border-collapse: collapse; }
    table.data td {
      padding: 7px 14px;
      border-top: 1px solid #f1f5f9;
      vertical-align: top;
    }
    table.data td:last-child {
      text-align: right;
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
      font-weight: 500;
    }
    .row-strong td { font-weight: 600; background: #f8fafc; }
    .row-total td {
      font-weight: 700;
      background: #eff6ff;
      border-top: 1px solid #bfdbfe;
      color: #1e40af;
    }
    .calc-head {
      background: linear-gradient(90deg, #334155, #475569);
    }
    .icon-calc, .icon-warn {
      width: 26px;
      height: 26px;
      border-radius: 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      font-size: 12pt;
      background: rgba(255,255,255,0.2);
    }
    .calc-card table.data .calc-label-cell {
      display: flex;
      align-items: baseline;
      gap: 6px;
    }
    .calc-sign {
      flex-shrink: 0;
      width: 1.1em;
      font-weight: 800;
      font-size: 11pt;
      line-height: 1;
      text-align: center;
    }
    .calc-sign-plus { color: #166534; }
    .calc-sign-minus { color: #991b1b; }
    .calc-sign-empty { visibility: hidden; }
    .calc-row-pos td {
      background: linear-gradient(90deg, #ecfdf5, #d1fae5) !important;
      border-top: 1px solid #6ee7b7;
      color: #14532d;
      font-weight: 600;
    }
    .calc-row-pos td:last-child {
      font-weight: 700;
      color: #166534;
      font-variant-numeric: tabular-nums;
    }
    .calc-row-neg td {
      background: linear-gradient(90deg, #fef2f2, #fee2e2) !important;
      border-top: 1px solid #fca5a5;
      color: #7f1d1d;
      font-weight: 600;
    }
    .calc-row-neg td:last-child {
      font-weight: 700;
      color: #991b1b;
      font-variant-numeric: tabular-nums;
    }
    .calc-row-neutral td {
      border-top: 1px solid #f1f5f9;
    }
    .debt-calc-title {
      font-weight: 700;
      line-height: 1.3;
    }
    .debt-calc-week {
      font-size: 8.5pt;
      color: #475569;
      margin-top: 2px;
      line-height: 1.3;
      font-weight: 500;
    }
    .debt-calc-desc {
      font-size: 8.5pt;
      color: #64748b;
      margin-top: 3px;
      line-height: 1.35;
      font-weight: 400;
    }
    .calc-summary {
      border-top: 2px solid #475569;
      padding: 10px 14px 12px;
      background: #f1f5f9;
    }
    .calc-summary-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      padding: 5px 0;
      font-size: 11pt;
    }
    .calc-summary-row strong {
      font-variant-numeric: tabular-nums;
      font-weight: 700;
    }
    .calc-summary-payout strong { color: #166534; }
    .calc-summary-salary strong { color: #1e293b; }
    .debts-card { border-color: #fecaca; }
    .debts-head {
      background: linear-gradient(90deg, #b91c1c, #dc2626);
    }
    .debts-table td { color: #991b1b; font-weight: 600; }
    .debts-open-row td {
      background: #fef2f2 !important;
      border-top: 1px solid #fecaca;
      vertical-align: top;
    }
    .debts-open-left { color: #b91c1c; font-weight: 600; }
    .debts-open-title {
      font-weight: 800;
      font-size: 11pt;
      color: #b91c1c;
      line-height: 1.25;
    }
    .debts-open-desc {
      font-size: 8.5pt;
      color: #991b1b;
      margin-top: 4px;
      font-weight: 500;
      line-height: 1.35;
      word-break: break-word;
    }
    .debts-open-value {
      font-weight: 800;
      font-size: 11pt;
      color: #b91c1c !important;
      font-variant-numeric: tabular-nums;
    }
    .footer {
      margin-top: 20px;
      padding-top: 10px;
      border-top: 1px solid #e2e8f0;
      font-size: 8.5pt;
      color: #64748b;
      text-align: center;
    }
  </style>
</head>
<body>
  <div class="wrap">
    <header class="hero">
      <div class="driver">${escapeHtml(p.driverLabel)}</div>
      <div class="week">${escapeHtml(p.weekLine)}</div>
    </header>
    ${servicesHtml}
    ${calcHtml}
    ${debtsHtml}
    ${p.footerLine ? `<p class="footer">${escapeHtml(p.footerLine)}</p>` : ""}
  </div>
</body>
</html>`;
}
